import Vue from 'vue'
import Vuex from 'vuex'
import axios from 'axios'

Vue.use(Vuex)

export default new Vuex.Store({
  state: {
    configuration: null,
    products: [],
    categories: [],
    brands: [],
    cart: [],
    user: null,
    loading: false,
    error: null,
    priceLists: [],
    featuredProducts: []
  },
  
  getters: {
    cartItemCount: state => {
      return state.cart.reduce((total, item) => total + item.quantity, 0)
    },
    cartTotal: state => {
      return state.cart.reduce((total, item) => total + (item.price * item.quantity), 0)
    },
    isAuthenticated: state => {
      return !!state.user
    },
    shopConfig: state => state.configuration
  },
  
  mutations: {
    SET_CONFIGURATION(state, config) {
      state.configuration = config
    },
    SET_PRODUCTS(state, products) {
      state.products = products
    },
    SET_CATEGORIES(state, categories) {
      state.categories = categories
    },
    SET_BRANDS(state, brands) {
      state.brands = brands
    },
    SET_FEATURED_PRODUCTS(state, products) {
      state.featuredProducts = products
    },
    ADD_TO_CART(state, product) {
      const existingItem = state.cart.find(item => item.id === product.id)
      if (existingItem) {
        existingItem.quantity += product.quantity || 1
      } else {
        state.cart.push({
          ...product,
          quantity: product.quantity || 1
        })
      }
    },
    REMOVE_FROM_CART(state, productId) {
      state.cart = state.cart.filter(item => item.id !== productId)
    },
    UPDATE_CART_QUANTITY(state, { productId, quantity }) {
      const item = state.cart.find(item => item.id === productId)
      if (item) {
        item.quantity = quantity
      }
    },
    CLEAR_CART(state) {
      state.cart = []
    },
    SET_USER(state, user) {
      state.user = user
    },
    SET_LOADING(state, loading) {
      state.loading = loading
    },
    SET_ERROR(state, error) {
      state.error = error
    },
    SET_PRICE_LISTS(state, lists) {
      state.priceLists = lists
    }
  },
  
  actions: {
    async loadConfiguration({ commit }) {
      try {
        commit('SET_LOADING', true)
        const response = await axios.get('/ecommerce/getConfiguration')
        if (response.data.status) {
          commit('SET_CONFIGURATION', response.data.data)
        }
      } catch (error) {
        commit('SET_ERROR', error.message)
        console.error('Error loading configuration:', error)
      } finally {
        commit('SET_LOADING', false)
      }
    },
    
    async loadProducts({ commit }, filters = {}) {
      try {
        commit('SET_LOADING', true)
        const response = await axios.post('/ecommerce/products/getProducts', filters)
        if (response.data.status) {
          commit('SET_PRODUCTS', response.data.data)
        }
      } catch (error) {
        commit('SET_ERROR', error.message)
        console.error('Error loading products:', error)
      } finally {
        commit('SET_LOADING', false)
      }
    },
    
    async loadFeaturedProducts({ commit }) {
      try {
        const response = await axios.get('/ecommerce/products/getFeatured')
        if (response.data.status) {
          commit('SET_FEATURED_PRODUCTS', response.data.data)
        }
      } catch (error) {
        console.error('Error loading featured products:', error)
      }
    },
    
    async loadCategories({ commit }) {
      try {
        const response = await axios.get('/ecommerce/getCategoryTree')
        if (response.data.status) {
          commit('SET_CATEGORIES', response.data.data)
        }
      } catch (error) {
        console.error('Error loading categories:', error)
      }
    },
    
    async loadBrands({ commit }) {
      try {
        const response = await axios.get('/ecommerce/getBrands')
        if (response.data.status) {
          commit('SET_BRANDS', response.data.data)
        }
      } catch (error) {
        console.error('Error loading brands:', error)
      }
    },
    
    addToCart({ commit }, product) {
      commit('ADD_TO_CART', product)
      // Guardar en localStorage
      const cart = this.state.cart
      localStorage.setItem('cart', JSON.stringify(cart))
    },
    
    removeFromCart({ commit }, productId) {
      commit('REMOVE_FROM_CART', productId)
      const cart = this.state.cart
      localStorage.setItem('cart', JSON.stringify(cart))
    },
    
    updateCartQuantity({ commit }, payload) {
      commit('UPDATE_CART_QUANTITY', payload)
      const cart = this.state.cart
      localStorage.setItem('cart', JSON.stringify(cart))
    },
    
    clearCart({ commit }) {
      commit('CLEAR_CART')
      localStorage.removeItem('cart')
    },
    
    loadCartFromStorage({ commit }) {
      const cart = localStorage.getItem('cart')
      if (cart) {
        const cartItems = JSON.parse(cart)
        cartItems.forEach(item => {
          commit('ADD_TO_CART', item)
        })
      }
    },
    
    async login({ commit }, credentials) {
      try {
        commit('SET_LOADING', true)
        const response = await axios.post('/ecommerce/login', credentials)
        if (response.data.success) {
          commit('SET_USER', response.data.user)
          localStorage.setItem('user', JSON.stringify(response.data.user))
          localStorage.setItem('token', response.data.token)
          axios.defaults.headers.common['Authorization'] = `Bearer ${response.data.token}`
          return { success: true }
        } else {
          return { success: false, message: response.data.message }
        }
      } catch (error) {
        console.error('Error during login:', error)
        return { success: false, message: error.message }
      } finally {
        commit('SET_LOADING', false)
      }
    },
    
    logout({ commit }) {
      commit('SET_USER', null)
      localStorage.removeItem('user')
      localStorage.removeItem('token')
      delete axios.defaults.headers.common['Authorization']
    },
    
    async register({ commit }, userData) {
      try {
        commit('SET_LOADING', true)
        const response = await axios.post('/ecommerce/register', userData)
        if (response.data.success) {
          return { success: true, message: response.data.message }
        } else {
          return { success: false, message: response.data.message }
        }
      } catch (error) {
        console.error('Error during registration:', error)
        return { success: false, message: error.message }
      } finally {
        commit('SET_LOADING', false)
      }
    },
    
    async createOrder({ commit, state }, orderData) {
      try {
        commit('SET_LOADING', true)
        const response = await axios.post('/ecommerce/createOrderEcommerce', {
          ...orderData,
          items: state.cart
        })
        if (response.data.success) {
          commit('CLEAR_CART')
          localStorage.removeItem('cart')
          return { success: true, orderId: response.data.orderId }
        } else {
          return { success: false, message: response.data.message }
        }
      } catch (error) {
        console.error('Error creating order:', error)
        return { success: false, message: error.message }
      } finally {
        commit('SET_LOADING', false)
      }
    }
  }
})